import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { useToast } from './Toast';
import { createRechargeRequest, listMyRechargeRequests, cancelRechargeRequest } from '../api';
import { RechargeRequest } from '../types';
import { creditsToYuan } from '../utils/currency';

interface RechargeRequestModalProps {
  open: boolean;
  onClose: () => void;
}

export default function RechargeRequestModal({ open, onClose }: RechargeRequestModalProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<RechargeRequest | null>(null);
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    listMyRechargeRequests(1, 1)
      .then(res => {
        const first = res.items?.[0];
        setPending(first && first.status === 'pending' ? first : null);
      })
      .catch(() => setPending(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open) {
      setAmount('');
      setRemark('');
      setError('');
      load();
    }
  }, [open, load]);

  const handleSubmit = async () => {
    const yuan = parseFloat(amount);
    if (!yuan || yuan <= 0) {
      setError(t('rechargeReq.amountPositive'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await createRechargeRequest(Math.round(yuan * 100), remark.trim());
      toast.success(t('rechargeReq.submitSuccess'));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rechargeReq.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id: string) => {
    setSubmitting(true);
    try {
      await cancelRechargeRequest(id);
      toast.success(t('rechargeReq.cancelSuccess'));
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('rechargeReq.cancelFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const footer = pending ? (
    <button className="btn btn-ghost" onClick={onClose}>{t('rechargeReq.close')}</button>
  ) : (
    <>
      <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>{t('rechargeReq.cancel')}</button>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? t('rechargeReq.submitting') : t('rechargeReq.submit')}
      </button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={t('rechargeReq.title')} width="440px" footer={footer}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>{t('rechargeReq.loading')}</div>
      ) : pending ? (
        <div>
          <p style={{ color: 'var(--text-2)', marginBottom: '0.75rem' }}>{t('rechargeReq.pendingHint')}</p>
          <div className="card" style={{ padding: '1rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
              ¥{creditsToYuan(pending.requestedAmount)}
            </div>
            {pending.remark && <div style={{ color: 'var(--text-2)', marginTop: '0.5rem' }}>{t('rechargeReq.notePrefix')}{pending.remark}</div>}
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginTop: '0.75rem', color: 'var(--danger)' }}
              onClick={() => handleCancel(pending.id)}
              disabled={submitting}
            >
              {t('rechargeReq.cancelApply')}
            </button>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: '0.75rem' }}>
            {t('rechargeReq.oneActiveHint')}
          </p>
        </div>
      ) : (
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '0.35rem' }}>
            {t('rechargeReq.amountLabel')}
          </label>
          <input
            type="number"
            className="input"
            style={{ width: '100%', marginBottom: '0.75rem' }}
            value={amount}
            min="0"
            step="0.01"
            placeholder={t('rechargeReq.amountPlaceholder')}
            onChange={e => setAmount(e.target.value)}
          />
          <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '0.35rem' }}>
            {t('rechargeReq.remarkLabel')}
          </label>
          <textarea
            className="input"
            style={{ width: '100%', minHeight: '64px', resize: 'vertical' }}
            value={remark}
            placeholder={t('rechargeReq.remarkPlaceholder')}
            onChange={e => setRemark(e.target.value)}
          />
          {error && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</div>}
        </div>
      )}
    </Modal>
  );
}
